import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { logger } from './utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================================
// Category metadata — single source of truth for icon, label, colour
// ============================================================
export const CATEGORY_META = {
  reference_request_sent:     { label: 'Reference Sent',       icon: '📤', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  reference_request_received: { label: 'Reference Received',   icon: '📥', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  reference_completed:        { label: 'Reference Complete',   icon: '✅', color: 'bg-green-50 text-green-700 border-green-200' },
  application_stage_changed:  { label: 'Stage Change',         icon: '📊', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  hm_decision:                { label: 'HM Decision',          icon: '⚖️', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  interview_scheduled:        { label: 'Interview Scheduled',  icon: '📅', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  interview_cancelled:        { label: 'Interview Cancelled',  icon: '❌', color: 'bg-red-50 text-red-700 border-red-200' },
  mention:                    { label: 'Mention',              icon: '💬', color: 'bg-pink-50 text-pink-700 border-pink-200' },
  system:                     { label: 'System',               icon: '🔔', color: 'bg-gray-50 text-gray-700 border-gray-200' },
};

const RELATIVE_TIME = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
};

// ============================================================
// MessageBell — header dropdown widget with unread badge
// Polls /messages/unread-count every 60s
// ============================================================
export function MessageBell({ onOpen }) {
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/messages/unread-count`);
      setUnread(data.unread || 0);
    } catch (err) {
      // Silent — bell shouldn't break the header if API is down
      logger.error('Failed to load unread count', err);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  // Re-fetch when any other component dispatches `vh:messages-changed`
  useEffect(() => {
    window.addEventListener('vh:messages-changed', refresh);
    return () => window.removeEventListener('vh:messages-changed', refresh);
  }, [refresh]);

  return (
    <button
      onClick={onOpen}
      className="relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 transition"
      data-testid="message-bell"
      title={`${unread} unread message${unread === 1 ? '' : 's'}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
      {unread > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-600 text-white flex items-center justify-center"
          data-testid="message-bell-badge"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}

// ============================================================
// MessageBoard — full inbox view with search, filter, mark-read
// ============================================================
export function MessageBoard() {
  const [messages, setMessages] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [readFilter, setReadFilter] = useState('all'); // 'all' | 'unread' | 'read'
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const searchDebounce = useRef(null);

  // ---- Load ----
  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeCategory !== 'all') params.category = activeCategory;
      if (readFilter === 'unread') params.read = false;
      if (readFilter === 'read') params.read = true;
      if (debouncedQuery) params.q = debouncedQuery;

      const [msgRes, catRes] = await Promise.all([
        axios.get(`${API}/messages`, { params }),
        axios.get(`${API}/messages/categories`),
      ]);
      setMessages(msgRes.data);
      setCategoryCounts(catRes.data);
    } catch (err) {
      logger.error('Failed to load messages', err);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, readFilter, debouncedQuery]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // ---- Debounce the search input by 300ms ----
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => searchDebounce.current && clearTimeout(searchDebounce.current);
  }, [searchQuery]);

  // ---- Mark as read when a message is selected ----
  const handleSelect = async (msg) => {
    setSelectedId(msg.id);
    if (!msg.read) {
      try {
        await axios.put(`${API}/messages/${msg.id}/read`);
        setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, read: true } : m)));
        window.dispatchEvent(new Event('vh:messages-changed'));
      } catch (err) {
        logger.error('Failed to mark read', err);
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/messages/mark-all-read`);
      loadMessages();
      window.dispatchEvent(new Event('vh:messages-changed'));
    } catch (err) {
      logger.error('Failed to mark all read', err);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      if (selectedId === id) setSelectedId(null);
      window.dispatchEvent(new Event('vh:messages-changed'));
    } catch (err) {
      logger.error('Failed to delete message', err);
    }
  };

  const totalUnread = useMemo(
    () => categoryCounts.reduce((sum, c) => sum + (c.unread || 0), 0),
    [categoryCounts]
  );
  const totalCount = useMemo(
    () => categoryCounts.reduce((sum, c) => sum + (c.total || 0), 0),
    [categoryCounts]
  );
  const selectedMessage = useMemo(
    () => messages.find((m) => m.id === selectedId) || null,
    [messages, selectedId]
  );

  return (
    <div className="flex h-screen bg-gray-50" data-testid="message-board">
      {/* Sidebar — categories */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b">
          <h1 className="text-xl font-bold text-gray-900">Inbox</h1>
          <p className="text-xs text-gray-500 mt-1">
            {totalUnread} unread · {totalCount} total
          </p>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          <CategoryButton
            label="All Messages" icon="📨" active={activeCategory === 'all'}
            onClick={() => setActiveCategory('all')}
            unread={totalUnread} total={totalCount}
            testid="messages-filter-all"
          />
          {Object.entries(CATEGORY_META).map(([cat, meta]) => {
            const stat = categoryCounts.find((c) => c.category === cat);
            if (!stat || stat.total === 0) return null;
            return (
              <CategoryButton
                key={cat} label={meta.label} icon={meta.icon}
                active={activeCategory === cat}
                onClick={() => setActiveCategory(cat)}
                unread={stat.unread} total={stat.total}
                testid={`messages-filter-${cat}`}
              />
            );
          })}
        </nav>
        <div className="p-3 border-t bg-gray-50">
          <button
            disabled
            title="Outlook sync coming soon — schema reserved"
            className="w-full text-xs px-3 py-2 rounded border border-dashed border-gray-300 text-gray-400 cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="messages-connect-outlook"
          >
            <span>📧</span> Connect Outlook (soon)
          </button>
        </div>
      </aside>

      {/* Middle — list */}
      <section className="w-96 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-3 border-b space-y-2">
          <input
            type="search"
            placeholder="Search title, body, candidate, company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="messages-search-input"
          />
          <div className="flex gap-2 items-center text-xs">
            {['all', 'unread', 'read'].map((f) => (
              <button
                key={f}
                onClick={() => setReadFilter(f)}
                className={`px-2 py-1 rounded ${
                  readFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
                data-testid={`messages-readfilter-${f}`}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
            <span className="flex-1" />
            {totalUnread > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-blue-600 hover:underline"
                data-testid="messages-mark-all-read"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-sm text-gray-400">Loading…</div>
          ) : messages.length === 0 ? (
            <EmptyState query={debouncedQuery} category={activeCategory} />
          ) : (
            messages.map((msg) => (
              <MessageRow
                key={msg.id}
                message={msg}
                selected={selectedId === msg.id}
                onSelect={() => handleSelect(msg)}
                onDelete={(e) => handleDelete(msg.id, e)}
              />
            ))
          )}
        </div>
      </section>

      {/* Right — detail */}
      <section className="flex-1 overflow-y-auto bg-gray-50">
        {selectedMessage ? (
          <MessageDetail message={selectedMessage} />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Select a message to read
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================
function CategoryButton({ label, icon, active, onClick, unread, total, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-left transition ${
        active ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'
      }`}
    >
      <span>{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {unread > 0 && (
        <span className="text-[10px] font-bold bg-red-600 text-white px-1.5 rounded-full">
          {unread}
        </span>
      )}
      <span className="text-[10px] text-gray-400">{total}</span>
    </button>
  );
}

function MessageRow({ message, selected, onSelect, onDelete }) {
  const meta = CATEGORY_META[message.category] || CATEGORY_META.system;
  return (
    <div
      onClick={onSelect}
      className={`px-3 py-3 border-b cursor-pointer transition group ${
        selected ? 'bg-blue-50' : 'hover:bg-gray-50'
      } ${!message.read ? 'border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'}`}
      data-testid={`message-row-${message.id}`}
    >
      <div className="flex items-start gap-2">
        <span className="text-lg mt-0.5">{meta.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className={`text-sm truncate ${!message.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
              {message.title}
            </h3>
            <span className="text-[10px] text-gray-400 whitespace-nowrap">
              {RELATIVE_TIME(message.created_at)}
            </span>
          </div>
          <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{message.body}</p>
          <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded mt-1 border ${meta.color}`}>
            {meta.label}
          </span>
        </div>
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-600 text-sm self-start"
          data-testid={`message-delete-${message.id}`}
          title="Delete"
        >
          ×
        </button>
      </div>
    </div>
  );
}

function MessageDetail({ message }) {
  const meta = CATEGORY_META[message.category] || CATEGORY_META.system;
  return (
    <article className="p-8 max-w-2xl mx-auto" data-testid={`message-detail-${message.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-xs px-2 py-1 rounded border ${meta.color}`}>
          {meta.icon} {meta.label}
        </span>
        <span className="text-xs text-gray-500">
          {new Date(message.created_at).toLocaleString()}
        </span>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">{message.title}</h2>
      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{message.body}</p>
      {message.link && (
        <a
          href={message.link}
          className="inline-block mt-6 px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          data-testid={`message-action-${message.id}`}
        >
          Open related item →
        </a>
      )}
      <div className="mt-8 pt-4 border-t text-xs text-gray-400">
        Message ID: {message.id}
        {message.related_entity_id && (
          <> · Related: {message.related_entity_type} / {message.related_entity_id}</>
        )}
      </div>
    </article>
  );
}

function EmptyState({ query, category }) {
  let msg = 'No messages yet.';
  if (query) msg = `No matches for "${query}".`;
  else if (category !== 'all') msg = 'No messages in this category.';
  return (
    <div className="p-8 text-center" data-testid="messages-empty">
      <div className="text-4xl mb-2">📭</div>
      <p className="text-sm text-gray-500">{msg}</p>
    </div>
  );
}
